import RelativeTime from "components/ui/RelativeTime";
import { differenceInYears, isSameMinute } from "date-fns";
import getBaseUrl from "lib/utils/getBaseUrl";
import { FormattedMessage } from "react-intl";
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon,
} from "react-share";

interface ContentHeaderProps {
  slug: string;
  title: string;
  description?: string | null;
  firstPublishedAt: Date;
  lastPublishedAt: Date | null;
  author: {
    name: string;
    imageUrl: string;
  };
}

export function ContentHeader({
  slug,
  title,
  description,
  author,
  firstPublishedAt,
  lastPublishedAt,
}: ContentHeaderProps) {
  const shareUrl = `${getBaseUrl()}/blog/${slug}`;
  return (
    <section className="space-y-4">
      <h1 className="text-xl font-bold tracking-tight text-zinc-900 dark:text-white md:text-3xl">{title}</h1>

      {description && <h2 className="text-base font-medium text-slate-600 dark:text-slate-300">{description}</h2>}

      <div className="flex flex-col gap-y-4 text-sm text-slate-700 md:flex-row">
        <div className="flex items-center gap-x-2">
          <img
            className="aspect-square size-14 rounded-full object-cover squircle lg:size-10"
            alt={author.name}
            src={author.imageUrl}
          />

          <div>
            <strong className="text-base font-bold dark:text-white">{author.name}</strong>

            <div className="hidden text-slate-500 dark:text-slate-300 lg:block">
              <FormattedMessage defaultMessage="Publicado em" id="fhf/n3" />{" "}
              <RelativeTime timeStamp={firstPublishedAt} showFullDate={true} />
              {lastPublishedAt &&
                differenceInYears(new Date(), lastPublishedAt) === 0 &&
                !isSameMinute(firstPublishedAt, lastPublishedAt) && (
                  <FormattedMessage
                    defaultMessage=" — atualizado {timeAgo}"
                    id="WZgZ56"
                    values={{
                      timeAgo: <RelativeTime timeStamp={lastPublishedAt} showFullDate={false} />,
                    }}
                  />
                )}
            </div>

            <div className="flex flex-col text-slate-500 dark:text-slate-300 lg:hidden">
              <span>
                <FormattedMessage defaultMessage="Publicado em" id="fhf/n3" />{" "}
                <RelativeTime timeStamp={firstPublishedAt} showFullDate={true} />
              </span>
              <span>
                {lastPublishedAt &&
                  differenceInYears(new Date(), lastPublishedAt) === 0 &&
                  !isSameMinute(firstPublishedAt, lastPublishedAt) && (
                    <FormattedMessage
                      defaultMessage="Atualizado {timeAgo}"
                      id="RCZHQm"
                      values={{
                        timeAgo: <RelativeTime timeStamp={lastPublishedAt} showFullDate={false} />,
                      }}
                    />
                  )}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-x-2 md:ml-auto">
          <FacebookShareButton url={shareUrl}>
            <FacebookIcon size={32} round={true} />
          </FacebookShareButton>
          <WhatsappShareButton url={shareUrl}>
            <WhatsappIcon size={32} round={true} />
          </WhatsappShareButton>
          <TwitterShareButton url={shareUrl}>
            <XIcon size={32} round={true} />
          </TwitterShareButton>
          <LinkedinShareButton url={shareUrl}>
            <LinkedinIcon size={32} round={true} />
          </LinkedinShareButton>
        </div>
      </div>
    </section>
  );
}
